"use client";
import { useEffect, useRef, useState } from "react";

interface CountUpProps {
  end: number;
  duration?: number;
  decimals?: number;
  suffix?: string;
  className?: string;
}

export default function CountUp({
  end,
  duration = 2000,
  decimals = 0,
  suffix = "",
  className,
}: CountUpProps) {
  const [displayValue, setDisplayValue] = useState(0);
  const ref = useRef<HTMLSpanElement | null>(null);
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Smoother easing than cubic
    const easeOutQuint = (t: number) => 1 - Math.pow(1 - t, 5);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || started.current) return;

        started.current = true;

        const animate = (timestamp: number) => {
          if (!startTimeRef.current) {
            startTimeRef.current = timestamp;
          }

          const elapsed = timestamp - startTimeRef.current;
          const progress = Math.min(elapsed / duration, 1);

          const easedProgress = easeOutQuint(progress);
          const nextValue = easedProgress * end;

          // Only update when visibly different (prevents tiny jitter)
          setDisplayValue((prev) => {
            if (Math.abs(prev - nextValue) < 0.001) return prev;
            return nextValue;
          });

          if (progress < 1) {
            animationRef.current = requestAnimationFrame(animate);
          } else {
            setDisplayValue(end); // clean final snap
          }
        };

        animationRef.current = requestAnimationFrame(animate);
      },
      {
        threshold: 0.65,
        rootMargin: "0px 0px -10% 0px", // feels more natural timing
      }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [end, duration]);

  return (
    <span ref={ref} className={className}>
      {displayValue.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}
      {suffix}
    </span>
  );
}